import { useState, useEffect } from 'react'
import { X } from 'lucide-react'
import { adminAPI } from '../services/api'

const UserModal = ({ user, mode, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    region: '',
    status: 'active'
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [availableRegions, setAvailableRegions] = useState([])

  useEffect(() => {
    // Загружаем список регионов
    fetchRegions()
    
    if (user) {
      setFormData({
        fullName: user.full_name || '',
        email: user.email || '',
        phone: user.phone || '',
        region: user.region || '',
        status: user.status || 'active'
      })
    }
  }, [user])

  // Блокируем скролл фона когда открыта модалка
  useEffect(() => {
    document.body.style.overflow = 'hidden'
    
    // Очищаем при размонтировании
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [])

  const fetchRegions = async () => {
    try {
      const response = await adminAPI.getRegions()
      if (response.data.success) {
        // Фильтруем только активные регионы
        const activeRegions = response.data.regions.filter(r => r.is_active)
        setAvailableRegions(activeRegions)
      }
    } catch (error) {
      console.error('Error fetching regions:', error)
    }
  }

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    try {
      const response = await adminAPI.updateUser(user.id, formData)

      if (response.data.success) {
        // Используем данные пользователя из ответа сервера (включая sync статус)
        const updatedUser = response.data.user || {
          ...user,
          full_name: formData.fullName,
          email: formData.email,
          phone: formData.phone,
          region: formData.region,
          status: formData.status,
          sync: false // При обновлении пользователь становится несинхронизированным
        }
        onSave(updatedUser)
      }
    } catch (error) {
      setError(error.response?.data?.message || 'Помилка при збереженні')
    } finally {
      setLoading(false)
    }
  }

  const isReadOnly = mode === 'view'
  const title = mode === 'edit' ? 'Редагувати користувача' : 'Перегляд користувача'

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {/* Тільки для перегляду - показуємо всі дані */}
          {isReadOnly && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                  ID
                </label>
                <input
                  type="text"
                  value={user?.id || ''}
                  className="input bg-gray-50"
                  readOnly
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Телефон
              </label>
              <input
                  type="text"
                  value={user?.phone || ''}
                  className="input bg-gray-50"
                  readOnly
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Повне ім'я
              </label>
              <input
                type="text"
                value={user?.full_name || ''}
                className="input bg-gray-50"
                readOnly
              />
            </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  value={user?.email || ''}
                  className="input bg-gray-50"
                  readOnly
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Номер картки
              </label>
              <input
                type="text"
                value={user?.card_number || ''}
                className="input bg-gray-50 font-mono"
                readOnly
              />
            </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Дата народження
                </label>
                <input
                  type="text"
                  value={user?.birth_date ? new Date(user.birth_date).toLocaleDateString('uk-UA', {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit'
                  }) : ''}
                  className="input bg-gray-50"
                  readOnly
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Регіон
                </label>
                <input
                  type="text"
                  value={user?.region || ''}
                  className="input bg-gray-50"
                  readOnly
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Баланс
                </label>
                <input
                  type="text"
                  value={user?.balance ? `${parseInt(user.balance).toLocaleString()} грн` : '0 грн'}
                  className="input bg-gray-50"
                  readOnly
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Знижка
                </label>
                <input
                  type="text"
                  value={user?.discount_percent ? `${user.discount_percent}%` : '0%'}
                  className="input bg-gray-50"
                  readOnly
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Статус
                </label>
                <input
                  type="text"
                  value={user?.status === 'active' ? 'Активний' : 
                         user?.status === 'blocked' ? 'Заблокований' : 'Невідомий'}
                  className="input bg-gray-50"
                  readOnly
                />
              </div>


              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Синхронізація
                </label>
                <input
                  type="text"
                  value={user?.sync ? 'Синхронізовано' : 'Не синхронізовано'}
                  className="input bg-gray-50"
                  readOnly
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Дата створення
                </label>
                <input
                  type="text"
                  value={user?.created_at ? new Date(user.created_at).toLocaleDateString('uk-UA', {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit'
                  }) : ''}
                  className="input bg-gray-50"
                  readOnly
                />
              </div>
            </div>
          )}

          {/* Для редагування - тільки поля що можна змінювати */}
          {!isReadOnly && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Повне ім'я
                </label>
                <input
                  type="text"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleChange}
                  placeholder="Іван Іванов"
                  className="input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="user@example.com"
                  className="input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Телефон
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="+380501234567"
                  className="input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Номер картки
                </label>
                <input
                  type="text"
                  value={user?.card_number || ''}
                  className="input bg-gray-50 font-mono"
                  readOnly
                  disabled
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Регіон
                </label>
                <select
                  name="region"
                  value={formData.region}
                  onChange={handleChange}
                  className="input"
                >
                  <option value="">Оберіть регіон</option>
                  {availableRegions.map((region) => (
                    <option key={region.id} value={region.name}>
                      {region.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Баланс
                </label>
                <input
                  type="text"
                  value={user?.balance ? `${parseInt(user.balance).toLocaleString()} грн` : '0 грн'}
                  className="input bg-gray-50"
                  readOnly
                  disabled
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Знижка
                </label>
                <input
                  type="text"
                  value={user?.discount_percent ? `${user.discount_percent}%` : '0%'}
                  className="input bg-gray-50"
                  readOnly
                  disabled
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Статус
                </label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className="input"
                >
                  <option value="active">Активний</option>
                  <option value="blocked">Заблокований</option>
                </select>
              </div>
            </div>
          )}

          {!isReadOnly && (
            <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={onClose}
                className="btn btn-secondary"
              >
                Скасувати
              </button>
              <button
                type="submit"
                disabled={loading}
                className="btn btn-primary"
              >
                {loading ? 'Збереження...' : 'Зберегти'}
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  )
}

export default UserModal
